import { BehaviorSubject, Subject } from "rxjs";

export enum ActiveFloatingActionButtonType {
	NONE = "NONE",
	NAVDRAWER = "NAVDRAWER",
	SEQUENCES = "SEQUENCES",
	DESCRIPTION = "DESCRIPTION",
}

export enum State {
	RESPONSIVE = "RESPONSIVE",
	FORCE_OPENED = "FORCE_OPENED",
	FORCE_CLOSED = "FORCE_CLOSED",
}

export interface ApplicationState {
	scrollDisabledChange: Subject<boolean>;
	navigationDrawerOpen: State;
	toolbarVisible: boolean;
	chatbotOpen: boolean;
	workspaceNavigationOpen: boolean;
	secondaryWorkspaceNavigationOpen: boolean;
	documentViewEnabled: boolean;
	isSearchOpen: BehaviorSubject<boolean>;
	isChatbotOpen: BehaviorSubject<boolean>;
	navigationDrawerState$: BehaviorSubject<State>;
	maskVisible: boolean;
	secondaryWorkspaceEnabled: boolean;
	scrollDisabled: boolean;
	activeFloatingActionButtonType: BehaviorSubject<ActiveFloatingActionButtonType>;

	closeSearch(): void;

	toggleSearch(): void;

	toggleWorkspaceNavigation(): void;

	toggleSecondaryWorkspaceNavigation(): void;

	closeChatbot(): void;

	toggleChatbot(): void;
}

export class VolatileApplicationState implements ApplicationState {
	navigationDrawerState$ = new BehaviorSubject<State>(State.RESPONSIVE);
	scrollDisabledChange = new Subject<boolean>();
	isSearchOpen = new BehaviorSubject(false);
	isChatbotOpen = new BehaviorSubject(false);
	activeFloatingActionButtonType = new BehaviorSubject<ActiveFloatingActionButtonType>(
		ActiveFloatingActionButtonType.NONE,
	);

	private _navigationDrawerOpen: State = State.RESPONSIVE;
	private _workspaceNavigationOpen!: boolean;
	secondaryWorkspaceNavigationOpen!: boolean;
	documentViewEnabled!: boolean;
	private _searchOpen!: boolean;
	private _chatbotOpen!: boolean;
	private _secondaryWorkspaceEnabled!: boolean;
	private _scrollDisabled!: boolean;
	private _maskVisible!: boolean;
	private _toolbarVisible!: boolean;

	get workspaceNavigationOpen(): boolean {
		return this._workspaceNavigationOpen ?? false;
	}

	set workspaceNavigationOpen(value: boolean) {
		this._workspaceNavigationOpen = value;
		if (value) {
			this.secondaryWorkspaceNavigationOpen = false;
		}
	}

	get secondaryWorkspaceEnabled(): boolean {
		return this._secondaryWorkspaceEnabled ?? false;
	}

	set secondaryWorkspaceEnabled(value: boolean) {
		this._secondaryWorkspaceEnabled = value;
		this.secondaryWorkspaceNavigationOpen = false;
	}

	get toolbarVisible(): boolean {
		return this._toolbarVisible ?? false;
	}

	set toolbarVisible(value: boolean) {
		this._toolbarVisible = value;
	}

	get chatbotOpen(): boolean {
		return this._chatbotOpen ?? false;
	}

	set chatbotOpen(value: boolean) {
		this._chatbotOpen = value;
	}

	get maskVisible(): boolean {
		return this._maskVisible ?? false;
	}

	set maskVisible(value: boolean) {
		this._maskVisible = value;
		this.scrollDisabled = value;
	}

	get navigationDrawerOpen(): State {
		return this._navigationDrawerOpen;
	}

	set navigationDrawerOpen(value: State) {
		this.maskVisible = value === State.FORCE_OPENED;
		this._navigationDrawerOpen = value;
		this.navigationDrawerState$.next(value);
	}

	closeSearch(): void {
		this._searchOpen = false;
		this.isSearchOpen.next(this._searchOpen);
	}

	toggleSearch(): void {
		this._searchOpen = !this._searchOpen;
		this.isSearchOpen.next(this._searchOpen);
	}

	closeChatbot(): void {
		this.chatbotOpen = false;
		this.isChatbotOpen.next(this._chatbotOpen);
	}

	toggleChatbot(): void {
		this.chatbotOpen = !this._chatbotOpen;
		this.isChatbotOpen.next(this._chatbotOpen);
	}

	toggleWorkspaceNavigation(): void {
		this.workspaceNavigationOpen = !this.workspaceNavigationOpen;
	}

	toggleSecondaryWorkspaceNavigation(): void {
		this.secondaryWorkspaceNavigationOpen = !(this.secondaryWorkspaceNavigationOpen && !this.workspaceNavigationOpen);
		this.workspaceNavigationOpen = false;
	}

	get scrollDisabled(): boolean {
		return this._scrollDisabled;
	}

	set scrollDisabled(value: boolean) {
		this._scrollDisabled = value;
		this.scrollDisabledChange.next(value);
	}
}
